'use client'

import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { focusRing } from '@repo/studio-design-system/utilities'
import { useIsMounted } from 'hooks/use-is-mounted'
import { Slot } from 'primitives/slot'
import { tw } from 'utils/classnames'
import { LoadingSpinner } from './loading-spinner'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'overlay'
  | 'danger'
  | 'promotional'
export type ButtonSize = 'xs' | 'sm' | 'md'

interface ButtonRootProps {
  variant?: ButtonVariant
  size?: ButtonSize
  asChild?: boolean
  loading?: boolean
}

type ButtonSharedProps = Omit<React.ComponentProps<'button'>, 'aria-label' | 'ref'> &
  ButtonRootProps & {
    ref?: React.Ref<HTMLButtonElement>
  }

export type ButtonProps<IconOnly extends boolean = false> = ButtonSharedProps &
  (IconOnly extends true
    ? {
        iconOnly: true
        'aria-label': string
      }
    : {
        iconOnly?: false
        'aria-label'?: string
      })

type IconOnlyButtonProps = ButtonProps<true>
type RegularButtonProps = ButtonProps

function ButtonRoot(props: IconOnlyButtonProps): React.ReactElement
function ButtonRoot(props: RegularButtonProps): React.ReactElement
function ButtonRoot({
  size = 'md',
  variant = 'primary',
  asChild,
  loading,
  iconOnly,
  ref,
  ...props
}: IconOnlyButtonProps | RegularButtonProps) {
  const className = tw(
    'rounded-2 outline-none',
    focusRing,
    'relative isolate',
    'flex items-center justify-center',
    props.disabled && 'cursor-not-allowed',
    iconOnly ? buttonClasses.iconOnlySizes[size] : buttonClasses.sizes[size],
    buttonClasses.variants[variant],
    // `tertiary` is the only variant that draws a border, and the height counts it. Trim the
    // vertical padding by that 1px either side so it measures the same as every other variant.
    !iconOnly && variant === 'tertiary' && buttonClasses.borderedSizes[size],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    props.className,
  )

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  if (loading) {
    hasSetLoading.current = true
  }

  return (
    <>
      <Comp {...props} className={tw(className)} ref={innerRef} />
      {hasSetLoading.current ? (
        <ButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}

function ButtonLoader({
  containerRef,
  loading,
  size,
}: {
  containerRef?: React.RefObject<HTMLButtonElement | null>
  loading?: boolean
  size: ButtonSize
}) {
  const isMounted = useIsMounted()

  if (!isMounted || !containerRef?.current) {
    return null
  }

  return ReactDOM.createPortal(
    <LoadingSpinner loading={loading} size={size} />,
    containerRef.current,
  )
}

export const Button = ButtonRoot

const buttonClasses = {
  variants: {
    primary: [
      'bg-theme_semantic_action_primary_background-color-default text-theme_semantic_action_primary_foreground-color-default',
      'hover:bg-theme_semantic_action_primary_background-color-hover hover:text-theme_semantic_action_primary_foreground-color-hover',
      'active:bg-theme_semantic_action_primary_background-color-active active:text-theme_semantic_action_primary_foreground-color-active',
      'disabled:bg-theme_semantic_action_primary_background-color-disabled disabled:text-theme_semantic_action_primary_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_primary_background-color-focus focus-visible:text-theme_semantic_action_primary_foreground-color-focus',
    ].join(' '),
    secondary: [
      'bg-theme_semantic_action_secondary_background-color-default text-theme_semantic_action_secondary_foreground-color-default',
      'hover:bg-theme_semantic_action_secondary_background-color-hover hover:text-theme_semantic_action_secondary_foreground-color-hover',
      'active:bg-theme_semantic_action_secondary_background-color-active active:text-theme_semantic_action_secondary_foreground-color-active',
      'disabled:bg-theme_semantic_action_secondary_background-color-disabled disabled:text-theme_semantic_action_secondary_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_secondary_background-color-focus focus-visible:text-theme_semantic_action_secondary_foreground-color-focus',
    ].join(' '),
    tertiary: [
      'bg-transparent border border-theme_semantic_action_tertiary_border-color-default text-theme_semantic_action_tertiary_foreground-color-default',
      'hover:bg-theme_semantic_action_tertiary_background-color-hover hover:text-theme_semantic_action_tertiary_foreground-color-hover',
      'active:bg-theme_semantic_action_tertiary_background-color-active active:text-theme_semantic_action_tertiary_foreground-color-active',
      'disabled:border-theme_semantic_action_tertiary_border-color-disabled disabled:text-theme_semantic_action_tertiary_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_tertiary_background-color-focus focus-visible:text-theme_semantic_action_tertiary_foreground-color-focus',
    ].join(' '),
    ghost: [
      'bg-transparent text-theme_semantic_action_ghost_foreground-color-default',
      'hover:bg-theme_semantic_action_ghost_background-color-hover hover:text-theme_semantic_action_ghost_foreground-color-hover',
      'active:bg-theme_semantic_action_ghost_background-color-active active:text-theme_semantic_action_ghost_foreground-color-active',
      'disabled:bg-theme_semantic_action_ghost_background-color-disabled disabled:text-theme_semantic_action_ghost_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_ghost_background-color-focus focus-visible:text-theme_semantic_action_ghost_foreground-color-focus',
    ].join(' '),
    overlay: [
      'bg-theme_semantic_action_inverse_background-color-default text-theme_semantic_action_inverse_foreground-color-default',
      'hover:bg-theme_semantic_action_inverse_background-color-hover hover:text-theme_semantic_action_inverse_foreground-color-hover',
      'active:bg-theme_semantic_action_inverse_background-color-active active:text-theme_semantic_action_inverse_foreground-color-active',
      'disabled:bg-theme_semantic_action_inverse_background-color-disabled disabled:text-theme_semantic_action_inverse_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_inverse_background-color-focus focus-visible:text-theme_semantic_action_inverse_foreground-color-focus',
    ].join(' '),
    danger: [
      'bg-theme_semantic_action_danger_background-color-default text-theme_semantic_action_danger_foreground-color-default',
      'hover:bg-theme_semantic_action_danger_background-color-hover hover:text-theme_semantic_action_danger_foreground-color-hover',
      'active:bg-theme_semantic_action_danger_background-color-active active:text-theme_semantic_action_danger_foreground-color-active',
      'disabled:bg-theme_semantic_action_danger_background-color-disabled disabled:text-theme_semantic_action_danger_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_danger_background-color-focus focus-visible:text-theme_semantic_action_danger_foreground-color-focus',
    ].join(' '),
    promotional: [
      'bg-theme_semantic_action_promotional_background-color-default text-theme_semantic_action_promotional_foreground-color-default',
      'hover:bg-theme_semantic_action_promotional_background-color-hover hover:text-theme_semantic_action_promotional_foreground-color-hover',
      'active:bg-theme_semantic_action_promotional_background-color-active active:text-theme_semantic_action_promotional_foreground-color-active',
      'disabled:bg-theme_semantic_action_promotional_background-color-disabled disabled:text-theme_semantic_action_promotional_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_promotional_background-color-focus focus-visible:text-theme_semantic_action_promotional_foreground-color-focus',
    ].join(' '),
  },
  // A button is as tall as its line box plus padding, so a label that wraps grows it rather than
  // spilling out. Each padding centres the line box in the height its size calls for:
  //   md (40 - 21) / 2 = 9.5   sm (32 - 21) / 2 = 5.5   xs (24 - 18) / 2 = 3
  //
  // The line box is pinned rather than inherited from the type scale, so a caller restyling the
  // text — `typography-13` on a dense card, say — moves the type within the button without
  // resizing the button. It is an arbitrary property because the class merge helper treats
  // `leading-*` as conflicting with `font-size`, so a `typography-*` merged after it would drop it.
  //
  // The vertical margin holds each icon's outer box to that same line box. That is what lets a
  // single padding serve every button: text, an icon, or both measure the same, so all three land
  // on the same height. md brings a 24px icon down to 21px; sm and xs bring 20px and 16px up.
  sizes: {
    md: [
      'py-[9.5px] px-4 [line-height:21px] gap-3',
      '[&_svg]:size-6 [&_svg]:-mx-1 [&_svg]:-my-[1.5px] typography-14',
    ].join(' '),
    sm: [
      'py-[5.5px] px-3 [line-height:21px] gap-2',
      '[&_svg]:size-5 [&_svg]:-mx-1 [&_svg]:my-[0.5px] typography-14',
    ].join(' '),
    xs: [
      'py-[3px] px-2 [line-height:18px] gap-2',
      '[&_svg]:size-4 [&_svg]:-mx-1 [&_svg]:my-px typography-12',
    ].join(' '),
  },
  // Paired with `variants.tertiary`, whose 1px border counts toward the height.
  borderedSizes: {
    md: 'py-[8.5px]',
    sm: 'py-[4.5px]',
    xs: 'py-[2px]',
  },
  iconOnlySizes: {
    md: ['box-border flex h-10 w-10 items-center justify-center px-2 [&>svg]:size-6'].join(' '),
    sm: ['box-border flex h-8 w-8 items-center justify-center px-1 [&>svg]:size-5'].join(' '),
    xs: ['box-border flex h-6 w-6 items-center justify-center px-1 [&>svg]:size-4'].join(' '),
  },
}
